import { ExternalLink, LoaderCircle, MessageCircleQuestion, Send, X } from 'lucide-react';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import useAskElevate from '@/hooks/useAskElevate';
import { cn } from '@/lib/utils';

import { renderMarkdown } from './markdown';

const SUGGESTED_QUESTIONS = [
	'How do I add a new course?',
	'How do AI outcome suggestions work?',
	'How do I invite a teacher and assign them a course?',
	'What does the submission and approval workflow look like?',
];

const AskElevate = () => {
	const [open, setOpen] = useState(false);
	const [input, setInput] = useState('');
	const { messages, ask, loading, streaming, error, reset } = useAskElevate();
	const busy = loading || streaming;

	// The course editor (/dashboard/courses/:courseId) has a bottom-right action bar
	// (Save Draft / Submit for Review). Raise the launcher there so it doesn't overlap.
	const { pathname } = useLocation();
	const inCourseEditor = /\/courses|submissions\/[^/]+$/.test(pathname);

	const scrollRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLTextAreaElement>(null);
	const lastUserMsgRef = useRef<HTMLDivElement>(null);

	const userMessageCount = messages.reduce((n, m) => (m.role === 'user' ? n + 1 : n), 0);
	const lastUserIndex = messages.reduce((acc, m, i) => (m.role === 'user' ? i : acc), -1);

	// When a new prompt is sent, anchor it to the top of the thread so the answer
	// streams in below it — instead of the view chasing the output to the bottom.
	// biome-ignore lint/correctness/useExhaustiveDependencies: userMessageCount is the intended trigger, not a read value
	useEffect(() => {
		lastUserMsgRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
	}, [userMessageCount]);

	useEffect(() => {
		if (open) inputRef.current?.focus();
	}, [open]);

	const submit = (question: string) => {
		void ask(question);
		setInput('');
	};

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		submit(input);
	};

	const hasConversation = messages.length > 0;

	// Only available inside the authenticated dashboard — not on public/family-facing
	// pages (sign in, alignment, course map, reset/set password, privacy).
	if (!pathname.startsWith('/dashboard')) {
		return null;
	}

	return (
		<>
			{/* Floating launcher */}
			{!open && (
				<button
					type="button"
					onClick={() => setOpen(true)}
					className={cn(
						'fixed right-5 z-50 flex items-center gap-2 rounded-full bg-purple-900 px-4 py-3 text-white shadow-lg transition-all hover:bg-purple-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-300',
						inCourseEditor ? 'bottom-14' : 'bottom-5'
					)}
					aria-label="Ask Elevate"
				>
					<MessageCircleQuestion className="h-5 w-5" />
					<span className="text-sm font-poppinsSemiBold">Ask Elevate</span>
				</button>
			)}

			{/* Chat panel */}
			{open && (
				<div className="fixed bottom-5 right-5 z-50 flex h-[min(620px,calc(100vh-2.5rem))] w-[min(420px,calc(100vw-2.5rem))] flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl">
					{/* Header */}
					<div className="flex items-center justify-between bg-purple-900 px-4 py-3 text-white">
						<div className="flex items-center gap-2">
							<div>
								<p className="text-sm font-poppinsSemiBold leading-tight">Ask Elevate</p>
								<p className="text-xs text-purple-200">Answers from the help center</p>
							</div>
						</div>
						<div className="flex items-center gap-1">
							{hasConversation && (
								<button
									type="button"
									onClick={reset}
									className="rounded-md px-2 py-1 text-xs text-purple-100 transition-colors hover:bg-purple-800"
								>
									New chat
								</button>
							)}
							<button
								type="button"
								onClick={() => setOpen(false)}
								className="rounded-md p-1 text-purple-100 transition-colors hover:bg-purple-800"
								aria-label="Close"
							>
								<X className="h-5 w-5" />
							</button>
						</div>
					</div>

					{/* Messages */}
					<div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
						{!hasConversation && (
							<div className="space-y-4">
								<div className="rounded-xl bg-purple-50 p-3 text-sm text-purple-900">
									Hi! I can answer questions about using Elevate using our help center. Ask me
									anything, or try one of these:
								</div>
								<div className="flex flex-col gap-2">
									{SUGGESTED_QUESTIONS.map((q) => (
										<button
											key={q}
											type="button"
											onClick={() => submit(q)}
											className="rounded-lg border border-border px-3 py-2 text-left text-sm transition-colors hover:border-purple-400 hover:bg-purple-50"
										>
											{q}
										</button>
									))}
								</div>
							</div>
						)}

						{messages.map((m, i) => (
							<div
								key={`${m.role}-${i}`}
								ref={i === lastUserIndex ? lastUserMsgRef : undefined}
								className={cn(
									'flex scroll-mt-4',
									m.role === 'user' ? 'justify-end' : 'justify-start'
								)}
							>
								{m.role === 'user' ? (
									<div className="max-w-[85%] rounded-2xl rounded-br-sm bg-purple-900 px-3 py-2 text-sm text-white">
										{m.content}
									</div>
								) : (
									<div className="max-w-[90%] space-y-2">
										<div
											className={cn(
												'kb-answer prose prose-sm max-w-none text-sm text-foreground',
												'rounded-2xl rounded-bl-sm bg-muted px-3 py-2',
												// explicit spacing/colors beat the app's global margin + list resets
												'prose-p:my-2 prose-p:leading-relaxed prose-p:first:mt-0 prose-p:last:mb-0',
												'prose-headings:text-purple-900 prose-headings:font-poppinsSemiBold prose-headings:mt-3 prose-headings:mb-1 prose-headings:first:mt-0',
												'prose-ol:my-2 prose-ul:my-2 prose-li:my-1 prose-li:marker:text-purple-500',
												'prose-strong:text-foreground prose-a:text-purple-700 prose-a:font-poppinsSemiBold prose-hr:my-3'
											)}
											// biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized via DOMPurify in renderMarkdown
											dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content) }}
										/>
										{m.sources && m.sources.length > 0 && (
											<div className="flex flex-col gap-1 pl-1">
												<span className="text-xs font-poppinsSemiBold text-muted-foreground">
													Sources
												</span>
												{m.sources.map((s) => (
													<a
														key={s.id}
														href={s.url}
														target="_blank"
														rel="noreferrer noopener"
														className="inline-flex items-center gap-1 text-xs text-purple-700 hover:underline"
													>
														<ExternalLink className="h-3 w-3 shrink-0" />
														{s.title}
													</a>
												))}
											</div>
										)}
									</div>
								)}
							</div>
						))}

						{loading && (
							<div className="flex justify-start">
								<div className="flex items-center gap-2 rounded-2xl rounded-bl-sm bg-muted px-3 py-2 text-sm text-muted-foreground">
									<LoaderCircle className="h-4 w-4 animate-spin" />
									Searching the help center…
								</div>
							</div>
						)}

						{error && <div className="text-center text-xs text-destructive">{error}</div>}
					</div>

					{/* Composer */}
					<form onSubmit={handleSubmit} className="border-t border-border p-3">
						<div className="flex items-end gap-2">
							<textarea
								ref={inputRef}
								value={input}
								onChange={(e) => setInput(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === 'Enter' && !e.shiftKey) {
										e.preventDefault();
										submit(input);
									}
								}}
								rows={1}
								placeholder="Ask a question…"
								className="max-h-28 flex-1 resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-300"
							/>
							<Button type="submit" size="icon" disabled={!input.trim() || busy} aria-label="Send">
								<Send className="h-4 w-4" />
							</Button>
						</div>
						<p className="mt-1.5 text-center text-[11px] text-muted-foreground">
							AI answers may be imperfect. Verify important steps.
						</p>
					</form>
				</div>
			)}
		</>
	);
};

export default AskElevate;
